import React from "react"

import {
	Grid,
	Typography,
	Button,
	Chip,
	Link
} from "@material-ui/core"
import {
	Done as PluginEnabledIcon,
	HighlightOff as PluginDisabledIcon
} from "@material-ui/icons"

import RDStationMarketingService from "@/services/RDStationMarketing"

import { Divider, Notification, Portlet, PopConfirm } from "@/components"
import PluginGroupPermissionGate from "@/components/PermissionGate/PluginGroupPermissionGate"

import { ReactComponent as TipsIcon } from "@/assets/icons/tips_icon.svg"

import { letalkLinks, rdStationMarketingLinks } from "@/utils/link"
import { RDStationMarketingPluginSettingsData } from "@/protocols/RdStationPluginProtocol"

import useStyles from "@/pages/Admin/Plugins/RDStationPlugin/RDStationMarketingPlugin/styles"
import { colors } from "@/styles/colors"
import ErrorHandlerService from "@/services/ErrorHandler"

type SettingsProps = {
	plugin: RDStationMarketingPluginSettingsData
	onUpdate: (data: Partial<RDStationMarketingPluginSettingsData>) => void
}

const Settings: React.FC<SettingsProps> = (props) => {
	const {
		plugin,
		onUpdate
	} = props

	const classes = useStyles()

	const handleUninstallPlugin = async () => {
		PopConfirm.open({
			title: "Desinstalar plugin do RD Station",
			description: "Deseja realmente desinstalar este plugin? Você pode instalá-lo novamente quando quiser, mas algumas configurações podem ser perdidas.",
			onConfirm: async () => {
				try {
					await RDStationMarketingService.revoke(plugin.id as number)

					onUpdate({
						...plugin,
						status: "disabled"
					})
				} catch (error) {
					ErrorHandlerService.handle(error as Error)
					Notification.error({ message: "Não foi possível desinstalar os dados do plugin." })
				}
			},
			confirmButtonText: "DESINSTALAR"
		})
	}

	const handleRDStationApiAuth = async () => {
		try {
			const response = await RDStationMarketingService.getEncryptStateData()

			const rdStationMarketingAuthUrl = rdStationMarketingLinks.getRdStationMarketingAuthUrl(
				rdStationMarketingLinks.rdStationMarketingCallback,
				response.RdStationMarketingStateToken
			)

			window.open(`${rdStationMarketingAuthUrl}`, "_self")
		} catch (error) {
			ErrorHandlerService.handle(error as Error)

			Notification.error({ message: "Não foi possível instalar os dados do plugin." })
		}
	}

	return (
		<Portlet>
			<Grid
				container
				direction="column"
				spacing={8}
			>
				<Grid
					item
					xs={12}
				>
					<Grid
						container
						direction="column"
						spacing={3}
					>
						<Grid item xs={12}>
							<Grid
								container
								justifyContent="space-between"
							>
								<Grid item>
									<Typography variant="h4">
										Acesso da API no RD Station
									</Typography>
									<Typography variant="body1">
										Cadastre a sua conta do RD Station Marketing à Letalk.
									</Typography>
								</Grid>

								<Grid item>
									<Chip
										size="small"
										onDelete={() => true}
										style={{
											pointerEvents: "none"
										}}
										{...(plugin?.status === "enabled" ? {
											label: "Plugin Instalado",
											className: classes.authPluginStatus,
											deleteIcon: <PluginEnabledIcon style={{ color: colors.gray[0] }} />
										} : {
											label: "Plugin Desinstalado",
											className: classes.unAuthPluginStatus,
											deleteIcon: <PluginDisabledIcon />
										})}
									/>
								</Grid>
							</Grid>
						</Grid>

						{plugin?.status === "enabled" && (
							<Grid
								item
								xs={12}
							>
								<Grid
									container
									className={classes.tipsContainer}
									justifyContent="flex-start"
								>
									<Grid
										item
										xs={12}
									>
										<Grid
											container
											justifyContent="flex-start"
											alignItems="center"
										>
											<TipsIcon className={classes.tipsIcon} />

											<Divider orientation="vertical" size={0.5} />

											<Typography className={classes.tipsHeaderText} >
												Dica
											</Typography>
										</Grid>
									</Grid>

									<Grid
										item
										xs={12}
									>
										<Grid
											container
											justifyContent="flex-start"
											alignItems="center"
										>
											<Typography className={classes.tipsContentText} >
												Agora que o seu plugin está ativado, você pode criar integrações da RD Station à Letalk{" "}
												<Link
													href={letalkLinks.wikiHowToCreateChatBotWithRDStationByIntegration}
													underline="always"
													target="_blank"
													color="inherit"
												>
													clique aqui e aprenda a como fazer
												</Link>
											</Typography>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Grid>

				<Grid
					item
					xs={12}
				>
					<Grid
						container
						justifyContent="flex-end"
					>
						<Grid item>
							<PluginGroupPermissionGate action={plugin?.status === "enabled" ? "disconnect" : "connect"}>
								<Button
									{...(plugin?.status === "enabled" ? {
										className: classes.authPluginButton
									} : {
										color: "primary"
									})}
									variant="contained"
									onClick={async () => {
										if (plugin?.status === "enabled") {
											await handleUninstallPlugin()
										} else {
											await handleRDStationApiAuth()
										}
									}}
								>
									{plugin?.status === "enabled" ? "Desinstalar Plugin" : "Instalar Plugin"}
								</Button>
							</PluginGroupPermissionGate>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Portlet>
	)
}

export default Settings
